<article
  class="daisy-card daisy-card-bordered bg-base-100 text-base-content shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden h-full flex flex-col border-base-300 group"
  (click)="onMoreDetailsClick(id)"
>
  <figure class="relative aspect-video w-full m-0 bg-base-200 overflow-hidden">
    <img
      [src]="imageBannerSource"
      [alt]="courseTitle"
      class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      (error)="onImageError()"
      #courseImage
      [class.hidden]="isImageError"
    />
    <div
      class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/50 bg-base-200"
      [class.hidden]="!isImageError"
    >
      <i class="fa-solid fa-image text-3xl"></i>
      <span class="text-xs font-bold tracking-wider">{{ getInitials(courseTitle) }}</span>
    </div>

    @if (subject) {
      <span class="absolute top-3 left-3 daisy-badge daisy-badge-primary font-bold text-[0.65rem] uppercase tracking-wider shadow-sm">
        {{ subject }}
      </span>
    }
  </figure>

  <section class="daisy-card-body p-4 sm:p-5 flex-1 flex flex-col gap-3">
    <!-- Top Group: Info & Schedule stick together at top -->
    <div class="flex flex-col gap-3">
      <div>
        <div class="flex items-center gap-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider mb-1">
          <span>{{ level || 'All Levels' }}</span>
          <span>•</span>
          <span>{{ medium ? medium + ' Medium' : 'English Medium' }}</span>
        </div>

        <h2 class="font-bold text-base sm:text-lg text-base-content line-clamp-2 leading-snug m-0 group-hover:text-primary transition-colors">
          {{ courseTitle }}
        </h2>

        <p class="text-xs sm:text-sm text-base-content/70 line-clamp-2 mt-1.5 leading-relaxed m-0">
          {{ description }}
        </p>
      </div>

      <!-- Schedule Block -->
      <div class="border-y border-base-300 py-2.5 my-1">
        <div class="flex justify-between items-center mb-1.5">
          <span class="text-[0.65rem] font-bold uppercase tracking-wider text-base-content/60">Schedule</span>
          @if (displayedSessions.length > 1) {
            <button
              type="button"
              class="daisy-btn daisy-btn-ghost daisy-btn-xs text-primary p-0 h-auto min-h-0"
              (click)="$event.stopPropagation(); toggleSessionDetails()"
              [attr.aria-label]="isSessionDetailsVisible ? 'Hide other schedules' : 'Show other schedules'"
            >
              <i class="fa-solid fa-chevron-down transition-transform duration-200" [class.rotate-180]="isSessionDetailsVisible"></i>
            </button>
          }
        </div>

        @if (displayedSessions.length > 0) {
          <div class="flex flex-col gap-2">
            @if (displayedSessions[0]; as primarySession) {
              <div class="flex flex-col gap-0.5 text-xs">
                <p class="font-bold text-base-content m-0 line-clamp-1">
                  {{ primarySession.name }}
                </p>
                <p class="text-base-content/70 text-[0.7rem] m-0">
                  {{ getRecurrenceSummaryPatternLabel(primarySession) }} • {{ getSessionTime(primarySession) }}
                </p>
                <p class="text-primary font-semibold text-[0.7rem] m-0">
                  Next: {{ getNextEventDate(primarySession) }}
                  @if (getSessionEventPosition(primarySession); as eventPosition) {
                    <span class="text-base-content/60 font-bold ml-1">• {{ eventPosition }}</span>
                  }
                </p>
              </div>

              @if (displayedSessions.length > 1) {
                <button
                  type="button"
                  class="text-left text-primary text-xs font-semibold hover:underline bg-transparent border-0 p-0 cursor-pointer w-fit"
                  (click)="$event.stopPropagation(); toggleSessionDetails()"
                >
                  @if (!isSessionDetailsVisible) {
                    <span>+{{ displayedSessions.length - 1 }} more schedules</span>
                  }
                </button>

                @if (isSessionDetailsVisible) {
                  @for (session of displayedSessions.slice(1); track session.name) {
                    <div class="flex flex-col gap-0.5 text-xs pt-1.5 border-t border-dashed border-base-300">
                      <p class="font-bold text-base-content m-0 line-clamp-1">
                        {{ session.name }}
                      </p>
                      <p class="text-base-content/70 text-[0.7rem] m-0">
                        {{ getRecurrenceSummaryPatternLabel(session) }} • {{ getSessionTime(session) }}
                      </p>
                      <p class="text-primary font-semibold text-[0.7rem] m-0">
                        Next: {{ getNextEventDate(session) }}
                        @if (getSessionEventPosition(session); as eventPosition) {
                          <span class="text-base-content/60 font-bold ml-1">• {{ eventPosition }}</span>
                        }
                      </p>
                    </div>
                  }

                  @if (extraSessionsCount > 0) {
                    <p class="text-xs text-primary font-semibold m-0">
                      +{{ extraSessionsCount }} more sessions
                    </p>
                  }
                }
              }
            }
          </div>
        } @else {
          <p class="text-xs text-base-content/50 m-0">No session details published yet</p>
        }
      </div>
    </div>

    <!-- Bottom Group: Next Session, Price & Action Button (Anchored to bottom) -->
    <div class="mt-auto flex flex-col gap-2">
      <div class="flex items-end justify-between gap-3 pt-1">
        <div class="min-w-0 flex flex-col">
          <span class="text-[0.6rem] font-bold uppercase tracking-wider text-base-content/60">Next Session</span>
          <span class="text-xs font-semibold text-primary truncate max-w-[180px]">{{ getNextSessionSummary() }}</span>
        </div>

        <div class="flex flex-col items-end shrink-0">
          <span class="text-lg sm:text-xl font-extrabold text-primary leading-tight">
            {{ formatPrice(price) }}
          </span>
          <span class="text-[0.6rem] font-bold uppercase tracking-wider text-base-content/60">per session</span>
        </div>
      </div>

      @if (isEnrolled) {
        <button
          type="button"
          class="daisy-btn daisy-btn-primary daisy-btn-sm w-full mt-1"
          (click)="$event.stopPropagation(); onClickAccessResources(id)"
        >
          <span>Continue Learning</span>
          <i class="fa-solid fa-arrow-right ml-1"></i>
        </button>
      }
    </div>
  </section>
</article>
